/**
 * ウィンドプロファイラ（横軸＝時間 / 縦軸＝高度 / 記号＝風向・風速）。
 *
 * 構想（開発メモ「ウィンドプロファイラー表示」）の
 *   ・向き＝風向  ・強さ＝風速
 * を、気象の高層風解析でおなじみの「風羽根（バーブ）」の見た目で表す
 * （利用者から実例のJMA高層風の図を示された。2026-08-23）。
 * 加えて、各点の南北成分を背景の色帯として敷き、南寄り/北寄りの強さが
 * 一目でわかるようにしてある。
 *
 * ★ 矢印（羽根）の向きの規約: この画面の「上」は**高度であって北ではない**。
 *   それでも記号は**方位として**描く（真上＝北へ流される）。気象の
 *   時間高度断面図の慣習と同じ。読み手が縦軸と取り違えないよう、
 *   隅に方位記号（N↑）を必ず出すこと。
 *
 * ★ 向きは「風が吹いてくる方向」ではなく「風に流される方向」で統一する。
 *   `WindSample.dir` 自体がその規約（packages/gv-core/src/types.ts）。
 *   気象学の慣習（矢羽根は吹いてくる方向を向く）とは逆になるが、断面ビュー
 *   など他の画面と向きの言語を揃えるため、あえて合わせていない
 *   （利用者に確認済み、2026-08-23）。先端に小さな矢じりを残しているのは、
 *   矢羽根だけだと「向き」が羽根の付き方からしか読めず分かりにくいため。
 *
 * ★ 速度の単位はkm/h。風羽根の目盛り自体は元々ノット（kt）基準の見た目だが、
 *   このアプリの他の読み値（対地速度など）と単位を揃えるため、
 *   短羽根=5km/h・長羽根=10km/h・旗=50km/h という、ktの見た目の比率を
 *   km/hの切りの良い数字に置き換えた独自の目盛りにしてある。
 *
 * DOM を触らない純粋関数にしてある（ui/summary.ts と同じ流儀）。
 */

import type { WindSample } from '@gv4d/core';
import { colorForAltitude, colorForTerminal, type AltitudeStop } from '../map/layers.ts';
import { metersToFeet } from '../units.ts';
import { toZonedTime, type TimeZoneId } from './timezone.ts';

export interface WindProfileSeries {
  terminalId: string;
  label: string;
  samples: WindSample[];
}

export interface WindProfileInput {
  series: WindProfileSeries[];
  /** 表示する時間範囲 [UTC ms] */
  from: number;
  to: number;
  /** 高度の目盛りと配色。会場ごとに変わるので呼び出し側から渡す */
  stops: AltitudeStop[];
  tz: TimeZoneId;
  width: number;
  height: number;
  /**
   * 背景の南北成分色帯1マスの大きさ。サンプルの間引き格子
   * （gridWindSamples の altBandM・timeBucketMs）と同じ値を渡すこと。
   * 色帯はサンプル点を中心に、この大きさの矩形として描く。
   */
  altBandM: number;
  timeBucketMs: number;
  /**
   * 表示する高度範囲 [m]（ピンチズーム用）。省略時は 0..topAltitudeM(...)
   * （今までどおりの全体表示）。
   */
  altMin?: number;
  altMax?: number;
}

/** 描画領域の余白。左は高度ラベル、下は時刻ラベルのぶん広く取る */
export const PAD = { top: 16, right: 16, bottom: 34, left: 54 };

/** 羽根の軸（シャフト）の長さ [px]。隣の格子へはみ出しすぎないように固定にする */
const SHAFT_LEN_PX = 22;
/** 先端の矢じりの長さ [px] */
const HEAD_LEN_PX = 4;
/** 旗根（full/half barb）1本の長さ [px] */
const TICK_LEN_PX = 7;
/** 旗根どうしの軸に沿った間隔 [px] */
const TICK_SPACING_PX = 5;
/** 軸の根元から最初の旗根までの余白 [px] */
const TICK_START_PX = 4;

/** km/h基準の羽根の目盛り。ktの見た目の比率(5/10/50)をそのまま流用する */
export const BARB_HALF_KMH = 5;
export const BARB_FULL_KMH = 10;
export const BARB_PENNANT_KMH = 50;

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * 表示する高度の上限 [m]。
 * 実データが色分けの最上段を超えることがあるので、両方を見て決める。
 */
export function topAltitudeM(series: WindProfileSeries[], stops: AltitudeStop[]): number {
  let max = stops.length > 0 ? stops[stops.length - 1].m : 0;
  for (const s of series) {
    for (const w of s.samples) if (w.alt > max) max = w.alt;
  }
  // 完全に0だと軸が潰れるので最低限の高さを持たせる
  return max > 0 ? max * 1.05 : 100;
}

/**
 * 南北成分（km/h、正=北へ・負=南へ）の色帯の境界値。
 *
 * JMAの実例は±45m/s（ジェット気流域）基準の刻みだが、気球運用で実際に
 * 出る風速はそれよりずっと小さいため、そのまま流用すると色の差が
 * ほとんど付かない。気球の運用域（0〜30km/h程度）に合わせて刻み直した。
 * 正=暖色（北へ）・負=寒色（南へ）。JMAの凡例「南→北＝正」と向きの意味は
 * 揃っている（このアプリの`dir`も「流される方向」で統一しているため）。
 */
export const NS_COLOR_STOPS: Array<{ maxAbsKmh: number; north: string; south: string }> = [
  { maxAbsKmh: 5, north: '#fdf1d6', south: '#dbf0fa' },
  { maxAbsKmh: 10, north: '#fbd79b', south: '#b3e2f7' },
  { maxAbsKmh: 15, north: '#f6ac62', south: '#78c6ef' },
  { maxAbsKmh: 20, north: '#ee7c3d', south: '#3ea7e0' },
  { maxAbsKmh: Infinity, north: '#d6531c', south: '#1a76bf' },
];

/** 南北成分の色帯の境界値（正の側のみ。凡例づくりにも使う） */
export const NS_COLOR_BAND_EDGES_KMH = NS_COLOR_STOPS.map((s) => s.maxAbsKmh).filter(Number.isFinite);

function northSouthColor(speedMs: number, dirDeg: number): string {
  const kmh = speedMs * 3.6;
  const rad = (dirDeg * Math.PI) / 180;
  const northComponentKmh = kmh * Math.cos(rad);
  const abs = Math.abs(northComponentKmh);
  for (const stop of NS_COLOR_STOPS) {
    if (abs <= stop.maxAbsKmh) return northComponentKmh >= 0 ? stop.north : stop.south;
  }
  return NS_COLOR_STOPS[NS_COLOR_STOPS.length - 1].north; // 到達しない（最後の境界はInfinity）
}

/** 羽根の内訳（貪欲分解）。5km/hに丸めてから、旗→長羽根→短羽根の順に割り当てる */
export interface BarbCounts {
  pennants: number;
  fullBarbs: number;
  halfBarb: boolean;
}

export function decomposeBarb(speedKmh: number): BarbCounts {
  let rem = Math.max(0, Math.round(speedKmh / BARB_HALF_KMH)) * BARB_HALF_KMH;
  const pennants = Math.floor(rem / BARB_PENNANT_KMH);
  rem -= pennants * BARB_PENNANT_KMH;
  const fullBarbs = Math.floor(rem / BARB_FULL_KMH);
  rem -= fullBarbs * BARB_FULL_KMH;
  const halfBarb = rem >= BARB_HALF_KMH;
  return { pennants, fullBarbs, halfBarb };
}

export function buildWindProfileSvg(input: WindProfileInput): string {
  const { series, from, to, stops, tz, width, height, altBandM, timeBucketMs } = input;

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);
  const spanMs = Math.max(1, to - from);
  const topM = topAltitudeM(series, stops);
  const viewAltMin = input.altMin ?? 0;
  const viewAltMax = input.altMax ?? topM;
  const altSpanM = Math.max(1, viewAltMax - viewAltMin);

  const xOf = (t: number) => PAD.left + ((t - from) / spanMs) * plotW;
  const yOf = (altM: number) => PAD.top + plotH - ((altM - viewAltMin) / altSpanM) * plotH;

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="100%" height="100%" role="img" aria-label="時間と高度ごとの風">`,
  );

  // --- 南北成分の背景色帯（軸・記号より先＝背面に描く） ---
  const cellW = Math.max(1, (timeBucketMs / spanMs) * plotW);
  const cellH = Math.max(1, (altBandM / altSpanM) * plotH);
  for (const s of series) {
    for (const w of s.samples) {
      if (w.t < from || w.t > to) continue;
      if (w.alt < viewAltMin || w.alt > viewAltMax) continue;
      const x = xOf(w.t);
      const y = yOf(w.alt);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const color = northSouthColor(w.speed, w.dir);
      parts.push(
        `<rect class="wp-ns-cell" x="${(x - cellW / 2).toFixed(1)}" y="${(y - cellH / 2).toFixed(1)}" ` +
          `width="${cellW.toFixed(1)}" height="${cellH.toFixed(1)}" fill="${color}" fill-opacity="0.6" />`,
      );
    }
  }

  // --- 高度の目盛り（既存の凡例と同じ段・同じ色） ---
  for (const stop of stops) {
    if (stop.m > topM) continue;
    if (stop.m < viewAltMin || stop.m > viewAltMax) continue;
    const y = yOf(stop.m);
    parts.push(
      `<line x1="${PAD.left}" y1="${y.toFixed(1)}" x2="${(PAD.left + plotW).toFixed(1)}" y2="${y.toFixed(1)}" ` +
        `stroke="${stop.color}" stroke-opacity="0.35" stroke-width="1" />`,
    );
    parts.push(
      `<text x="${PAD.left - 6}" y="${(y + 3.5).toFixed(1)}" text-anchor="end" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">${stop.ft}</text>`,
    );
  }
  parts.push(
    `<text x="4" y="${(PAD.top - 5).toFixed(1)}" font-size="9" fill="currentColor" ` +
      `fill-opacity="0.5">ft</text>`,
  );

  // --- 時刻の目盛り ---
  const tickCount = 4;
  for (let i = 0; i <= tickCount; i++) {
    const t = from + (spanMs * i) / tickCount;
    const x = xOf(t);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${PAD.top}" x2="${x.toFixed(1)}" y2="${(PAD.top + plotH).toFixed(1)}" ` +
        `stroke="currentColor" stroke-opacity="0.15" stroke-width="1" />`,
    );
    const anchor = i === 0 ? 'start' : i === tickCount ? 'end' : 'middle';
    parts.push(
      `<text x="${x.toFixed(1)}" y="${(PAD.top + plotH + 15).toFixed(1)}" text-anchor="${anchor}" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">` +
        `${escapeXml(toZonedTime(t, tz).time.slice(0, 5))}</text>`,
    );
  }

  // --- 風羽根（バーブ） ---
  let drawn = 0;
  for (const s of series) {
    const stroke = colorForTerminal(s.terminalId);
    for (const w of s.samples) {
      if (w.t < from || w.t > to) continue;
      if (w.alt < viewAltMin || w.alt > viewAltMax) continue;
      const x = xOf(w.t);
      const y = yOf(w.alt);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const data = { terminalId: s.terminalId, label: s.label, t: w.t, dir: w.dir, speed: w.speed, alt: w.alt };
      // 羽根は細い線で指では狙いにくいので、同じ位置に透明な当たり判定の
      // 円を重ねる（見た目には出ない。クリックはどちらから拾っても良い）
      parts.push(hitCircle(x, y, data));
      parts.push(barbGlyph(x, y, w.dir, w.speed, colorForAltitude(w.alt, stops), stroke, data));
      drawn++;
    }
  }

  // --- 方位記号（縦軸＝高度と取り違えないための目印） ---
  const nx = width - PAD.right - 14;
  const ny = PAD.top + 14;
  parts.push(
    `<g opacity="0.75">` +
      `<line x1="${nx}" y1="${ny + 9}" x2="${nx}" y2="${ny - 7}" stroke="currentColor" stroke-width="1.2" />` +
      `<path d="M ${nx} ${ny - 10} L ${nx - 3.2} ${ny - 4} L ${nx + 3.2} ${ny - 4} Z" fill="currentColor" />` +
      `<text x="${nx}" y="${ny + 20}" text-anchor="middle" font-size="9" fill="currentColor">N</text>` +
      `</g>`,
  );

  if (drawn === 0) {
    parts.push(
      `<text x="${(width / 2).toFixed(1)}" y="${(height / 2).toFixed(1)}" text-anchor="middle" ` +
        `font-size="12" fill="currentColor" fill-opacity="0.55">まだ風のデータがありません</text>`,
    );
  }

  parts.push('</svg>');
  return parts.join('');
}

/** 羽根1本ぶんの、選択時に読み出すための値 */
interface WindArrowData {
  terminalId: string;
  label: string;
  t: number;
  dir: number;
  speed: number;
  alt: number;
}

/** data-* 属性の文字列を組み立てる。羽根本体・当たり判定の円で共用する */
function dataAttrs(data: WindArrowData): string {
  return (
    `data-terminal="${escapeXml(data.terminalId)}" data-label="${escapeXml(data.label)}" ` +
    `data-t="${data.t}" data-dir="${data.dir}" data-speed="${data.speed}" data-alt="${data.alt}"`
  );
}

/**
 * 透明な当たり判定の円。羽根は細い線で指では狙いにくいため、見た目に出ない
 * 広めの円を同じ位置に重ねてタップを拾いやすくする。
 */
function hitCircle(x: number, y: number, data: WindArrowData): string {
  return (
    `<circle class="wp-hit" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="12" ` +
    `fill="transparent" ${dataAttrs(data)} />`
  );
}

/**
 * 矢印の形（"d" パス文字列）を組み立てる。(x,y) を根元にして dir の方位へ
 * 伸ばす。矢じりの大きさ(headLen)を独立させてあるのは、チャート内の細い
 * 矢印（頭が相対的に大きいと潰れて見える）と、単体で埋め込む拡大矢印
 * （頭を長さに比例させないと逆に貧弱に見える）とで、太さの好ましい比率が
 * 違うため。
 */
function arrowD(x: number, y: number, dirDeg: number, len: number, headLen: number): string {
  const rad = (dirDeg * Math.PI) / 180;
  // 画面は上が北。方位0°(北)で -y 方向へ伸ばす
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const tipX = x + dx * len;
  const tipY = y + dy * len;
  // 矢じり（進行方向に直交する向きに開く）
  const px = -dy;
  const py = dx;
  const baseX = tipX - dx * headLen;
  const baseY = tipY - dy * headLen;

  const f = (n: number) => n.toFixed(1);
  return (
    `M ${f(x)} ${f(y)} L ${f(baseX)} ${f(baseY)} ` +
    `M ${f(tipX)} ${f(tipY)} L ${f(baseX + px * headLen * 0.6)} ${f(baseY + py * headLen * 0.6)} ` +
    `L ${f(baseX - px * headLen * 0.6)} ${f(baseY - py * headLen * 0.6)} Z`
  );
}

/**
 * 単体で埋め込める拡大矢印（ポップアップ・下の窓の読み値ボックスで使う）。
 *
 * チャート内の風羽根（`barbGlyph`）とは別物。単体表示では風速は既に数値
 * （km/h）で併記されるため、向きだけを大きな矢印1本で見せれば十分という
 * 判断で、羽根化はしていない（2026-08-23、チャートのみ羽根化した際の
 * 判断）。向きの計算ロジックは共通（`arrowD`）。
 */
export function buildDirectionArrowSvg(
  dirDeg: number,
  opts: { size: number; fill: string; stroke: string },
): string {
  const { size, fill, stroke } = opts;
  const cx = size / 2;
  const cy = size / 2;
  const len = size * 0.42;
  const headLen = len * 0.4; // 拡大矢印は頭を長さに比例させる（チャート内の頭打ちとは別物）
  const d = arrowD(cx, cy, dirDeg, len, headLen);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" ` +
    `width="${size}" height="${size}" role="img" aria-label="風向 ${Math.round(dirDeg)}度">` +
    `<path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${Math.max(1.5, size * 0.03)}" ` +
    `stroke-linecap="round" stroke-linejoin="round" /></svg>`
  );
}

/**
 * 羽根の目盛り（短5・長10・旗50 km/h）の凡例を、実際にチャートで使うのと
 * 同じ`barbGlyph`で組み立てる。目盛りを変えてもここが自動で追従する。
 */
export function buildBarbLegendSvg(color: string): string {
  const examples: Array<{ kmh: number; label: string }> = [
    { kmh: BARB_HALF_KMH, label: `${BARB_HALF_KMH}` },
    { kmh: BARB_FULL_KMH, label: `${BARB_FULL_KMH}` },
    { kmh: BARB_PENNANT_KMH, label: `${BARB_PENNANT_KMH}` },
  ];
  const cellW = 46;
  const width = cellW * examples.length;
  const height = 44;
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="${width}" height="${height}" role="img" aria-label="羽根の目盛り">`,
  ];
  examples.forEach((ex, i) => {
    const cx = cellW * i + cellW / 2;
    const cy = 26;
    const data = { terminalId: '', label: '', t: 0, dir: 0, speed: ex.kmh / 3.6, alt: 0 };
    parts.push(barbGlyph(cx, cy, 0, ex.kmh / 3.6, color, color, data));
    parts.push(
      `<text x="${cx}" y="${height - 2}" text-anchor="middle" font-size="9" ` +
        `fill="currentColor" fill-opacity="0.65">${ex.label}</text>`,
    );
  });
  parts.push('</svg>');
  return parts.join('');
}

/** kmh（符号付き、正=北・負=南）から凡例用の色を直接引く（`northSouthColor`の速度/向き経由を介さない版） */
function colorForNsValue(kmh: number): string {
  const abs = Math.abs(kmh);
  for (const stop of NS_COLOR_STOPS) {
    if (abs <= stop.maxAbsKmh) return kmh >= 0 ? stop.north : stop.south;
  }
  return NS_COLOR_STOPS[NS_COLOR_STOPS.length - 1].north;
}

/**
 * 南北成分の色帯の凡例（水平の帯＋境界値のラベル）。JMAの実例と同じ
 * 「南→北＝正」の向きで、左=南（寒色）・右=北（暖色）に並べる。
 */
export function buildNsColorLegendSvg(width: number): string {
  const height = 30;
  const barY = 4;
  const barH = 12;
  const edges = [...NS_COLOR_BAND_EDGES_KMH].sort((a, b) => a - b); // [5,10,15,20]
  const maxKmh = edges[edges.length - 1] * 1.6; // 最後の帯にも幅を持たせる
  // 中央(0)から左右対称に境界を並べる: [-max,-20,-15,-10,-5, 0, 5,10,15,20, max]
  const boundaries = [-maxKmh, ...edges.map((e) => -e).reverse(), 0, ...edges, maxKmh];
  const xOf = (kmh: number) => width / 2 + (kmh / maxKmh) * (width / 2);

  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="${width}" height="${height}" role="img" aria-label="南北成分の色帯">`,
  ];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const mid = (boundaries[i] + boundaries[i + 1]) / 2;
    const x0 = xOf(boundaries[i]);
    const x1 = xOf(boundaries[i + 1]);
    parts.push(
      `<rect x="${x0.toFixed(1)}" y="${barY}" width="${(x1 - x0).toFixed(1)}" height="${barH}" ` +
        `fill="${colorForNsValue(mid)}" />`,
    );
  }
  for (const e of [0, ...edges]) {
    for (const kmh of e === 0 ? [0] : [-e, e]) {
      parts.push(
        `<text x="${xOf(kmh).toFixed(1)}" y="${barY + barH + 11}" text-anchor="middle" font-size="8" ` +
          `fill="currentColor" fill-opacity="0.6">${kmh}</text>`,
      );
    }
  }
  parts.push(
    `<text x="2" y="${barY + barH / 2 + 3}" font-size="8" fill="currentColor" fill-opacity="0.6">南</text>`,
  );
  parts.push(
    `<text x="${width - 2}" y="${barY + barH / 2 + 3}" text-anchor="end" font-size="8" ` +
      `fill="currentColor" fill-opacity="0.6">北</text>`,
  );
  parts.push('</svg>');
  return parts.join('');
}

/**
 * チャート内1点ぶんの風羽根。(x,y) を根元にして dir の方位へ軸を伸ばし、
 * 先端に小さな矢じり、根元寄りに風速に応じた旗根（羽根・旗）を立てる。
 *
 * 塗り＝高度色、輪郭＝機体色（地図の「航跡＝高度色／端末マーカー＝機体色」
 * と同じ使い分け）。軸そのものは線なので塗りの効果は見えないが、既存の
 * 「塗り＝高度色」の約束ごとを保つため、軸のpathにも fill 属性を残す。
 *
 * 風速が丸めて0km/hになる（無風）場合は、慣習にならい小さな丸だけを描く。
 */
function barbGlyph(
  x: number,
  y: number,
  dirDeg: number,
  speedMs: number,
  fill: string,
  stroke: string,
  data: WindArrowData,
): string {
  const speedKmh = speedMs * 3.6;
  const { pennants, fullBarbs, halfBarb } = decomposeBarb(speedKmh);

  if (pennants === 0 && fullBarbs === 0 && !halfBarb) {
    // 無風記号（丸）。既存の矢印と同じ class を持たせ、既存のクリック配線
    // （.wp-arrow を拾う）をそのまま使えるようにする
    return (
      `<circle class="wp-arrow" ${dataAttrs(data)} cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" ` +
      `fill="none" stroke="${stroke}" stroke-width="1.3" />`
    );
  }

  const rad = (dirDeg * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  // 旗根を立てる向き（軸に直交。arrowD の矢じりの開き方向と同じ計算）
  const px = -dy;
  const py = dx;

  const tipX = x + dx * SHAFT_LEN_PX;
  const tipY = y + dy * SHAFT_LEN_PX;
  const f = (n: number) => n.toFixed(1);

  const parts: string[] = [];

  // 軸（根元→先端）。data-* と class="wp-arrow" はここに持たせる
  // （クリック検出・既存テストの d="M x y L x y" 抽出パターンをそのまま使う）
  parts.push(
    `<path class="wp-arrow" ${dataAttrs(data)} d="M ${f(x)} ${f(y)} L ${f(tipX)} ${f(tipY)}" ` +
      `fill="${fill}" stroke="${stroke}" stroke-width="1.3" stroke-linecap="round" />`,
  );

  // 先端の小さな矢じり（向きを一目で分かるようにする。気象学の慣習には無いが、
  // このアプリは「流される方向」の矢印言語を保つためあえて残している）
  const headBaseX = tipX - dx * HEAD_LEN_PX;
  const headBaseY = tipY - dy * HEAD_LEN_PX;
  parts.push(
    `<path d="M ${f(tipX)} ${f(tipY)} ` +
      `L ${f(headBaseX + px * HEAD_LEN_PX * 0.6)} ${f(headBaseY + py * HEAD_LEN_PX * 0.6)} ` +
      `L ${f(headBaseX - px * HEAD_LEN_PX * 0.6)} ${f(headBaseY - py * HEAD_LEN_PX * 0.6)} Z" ` +
      `fill="${fill}" />`,
  );

  // 旗根（根元寄りから、旗→長羽根→短羽根の順に並べる）
  let pos = TICK_START_PX;
  for (let i = 0; i < pennants; i++) {
    const bx = x + dx * pos;
    const by = y + dy * pos;
    const nx2 = bx + dx * TICK_SPACING_PX * 0.8;
    const ny2 = by + dy * TICK_SPACING_PX * 0.8;
    parts.push(
      `<path class="wp-barb-pennant" d="M ${f(bx)} ${f(by)} L ${f(nx2)} ${f(ny2)} ` +
        `L ${f(bx + px * TICK_LEN_PX)} ${f(by + py * TICK_LEN_PX)} Z" fill="${fill}" />`,
    );
    pos += TICK_SPACING_PX;
  }
  for (let i = 0; i < fullBarbs; i++) {
    const bx = x + dx * pos;
    const by = y + dy * pos;
    parts.push(
      `<path class="wp-barb-full" d="M ${f(bx)} ${f(by)} ` +
        `L ${f(bx + px * TICK_LEN_PX)} ${f(by + py * TICK_LEN_PX)}" ` +
        `stroke="${stroke}" stroke-width="1.3" stroke-linecap="round" fill="none" />`,
    );
    pos += TICK_SPACING_PX;
  }
  if (halfBarb) {
    const bx = x + dx * pos;
    const by = y + dy * pos;
    parts.push(
      `<path class="wp-barb-half" d="M ${f(bx)} ${f(by)} ` +
        `L ${f(bx + px * TICK_LEN_PX * 0.5)} ${f(by + py * TICK_LEN_PX * 0.5)}" ` +
        `stroke="${stroke}" stroke-width="1.3" stroke-linecap="round" fill="none" />`,
    );
  }

  return parts.join('');
}
